import React, {useState, useEffect, useCallback} from 'react';
import filter from 'lodash/filter';
import moment from 'moment';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';

import useDateInterval from '../../hooks/useDateInterval';
import {withData} from '../../containers/DataContainer';
import NewDelivery from '../../components/Forms/NewDelivery';
import BackButton from '../../components/Buttons/BackButton';
import {
  DeliveriesHeader,
  DeliveriesTable
} from '../../components/Tables/Deliveries';
import Error from '../../components/Forms/Error';
import useStyles from '../styles';

import DeliveryDetails from './Details';

const View = withData(({
  createDelivery,
  fetchDeliveriesByDate,
  fetchMeta,
}) => {

  const classes = useStyles();

  const [path, setPath] = useState('root');
  const [isDeliveryBusy, setDeliveryBusy] = useState(true);
  const [isError, setError] = useState(false);
  const [startDate, setStartDate, endDate, setEndDate] = useDateInterval({
    startDate: moment().startOf('day'),
    endDate: moment().endOf('day')
  });

  const fetchData = useCallback(async () => {
    setDeliveryBusy(true);
    await Promise.all([
      fetchDeliveriesByDate({
        startDate: '' + startDate.valueOf(),
        endDate: '' + Math.max(endDate.valueOf(), startDate.valueOf())
      }),
      fetchMeta()
    ]);
    setDeliveryBusy(false);
  }, [startDate, endDate, fetchDeliveriesByDate, setDeliveryBusy, fetchMeta]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const _createDelivery = async state => {
    try {
      await createDelivery(state);
      await fetchData();
      setPath('root');
    } catch (e) {
      if (
        e?.response?.status === 403 &&
        e?.response?.data === 'Delivery id already exists.'
      ) setError(true);
      else {
        setPath('root');
        throw e;
      }
    }
  };

  return <>
    <Paper className={classes.paper}>
      {path === 'new'
        ? <Grid container>
          <Grid item>
            <BackButton onClick={() => setPath('root')}/>
          </Grid>
          <NewDelivery submitForm={_createDelivery}/>
        </Grid>
        : <Grid container>
          <Grid item xs={12}>
            <DeliveriesHeader
              toggleNewForm={() => setPath('new')}
              {...{startDate, setStartDate, endDate, setEndDate}}
            />
          </Grid>
          <Grid item xs={12}>
            <Deliveries {...{isDeliveryBusy, fetchData}}/>
          </Grid>
        </Grid>
      }
    </Paper>
    <Error
      message={'Pré Carga já existe. Verifique o identificador.'}
      isOpen={isError}
      onClose={() => setError(false)}
    />
  </>;
});

export const Deliveries = ({isDeliveryBusy, fetchData}) => {

  const [path, setPath] = useState('root');
  const [isDetailBusy, setDetailBusy] = useState(true);
  const [rowData, setRowData] = useState({});

  return <>
    <DeliveriesTableComponent
      {...{
        path,
        setPath,
        setRowData,
        setDetailBusy,
        isDeliveryBusy
      }}
    />
    <DeliveriesDetailComponent
      {...{
        rowData,
        isDetailBusy,
        setDetailBusy,
        path,
        setPath,
        fetchData
      }}
    />
  </>;
};

export const DeliveriesTableComponent = withData(({
  data,
  meta,
  setPath,
  setSlots,
  setRowData,
  setDetailBusy,
  isDeliveryBusy,
  height,
}) => {

  const deliveries = meta?._id
    ? filter(data.deliveries, {vehicleId: meta._id})
    : data.deliveries;

  const onRowClick = ({rowData}) => {
    setDetailBusy(true);
    setPath('detail');
    setRowData(rowData);
    setSlots(rowData.slotData)
    setDetailBusy(false);
  };

  return <DeliveriesTable
    data={deliveries}
    isBusy={isDeliveryBusy}
    {...{onRowClick, height}}
  />;
});

export const DeliveriesDetailComponent = withData(({
  path,
  rowData,
  setPath,
  isDetailBusy,
  setDetailBusy,
  updateDeliveryStatus,
  updateDeliveryVehicle,
  deleteDelivery,
  createTransfer,
  createEvent,
  fetchData,
}) => {

  const _deleteDelivery = async () => {
    setDetailBusy(true);
    await deleteDelivery(rowData);
    setPath('root');
    setDetailBusy(false);
  };

  const _createTransfer = async data => {
    setDetailBusy(true);
    await createTransfer({
      ...rowData,
      ...data
    });
    setPath('root');
    await fetchData();
    setDetailBusy(false);
  };

  const _updateDeliveryStatus = async () => {
    setDetailBusy(true);
    await updateDeliveryStatus({
      ...rowData,
      status: 'done'
    });
    setPath('root');
    setDetailBusy(false);
  };

  const _createEvent = async data => {
    setDetailBusy(true);
    await createEvent({
      ...rowData,
      event: data
    });
    setPath('root');
    await fetchData();
    setDetailBusy(false);
  };

  return <DeliveryDetails
    {...{
      path,
      setPath,
      rowData,
      isDetailBusy,
      _updateDeliveryStatus,
      _deleteDelivery,
      _createEvent,
      _createTransfer,
      fetchData,
    }}
  />;
});

export default View;
